import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, BrainCircuit, Download, FileText, FileDown, ExternalLink, ImageIcon, AlertTriangle } from "lucide-react";
import { api, mediaUrl, downloadUrl } from "../lib/api";

const SUGGESTIONS = [
  "Un détective insomniaque dans une ville où il pleut sans cesse",
  "Une pianiste aveugle entend un meurtre chez son voisin",
  "Deux astronautes découvrent une porte au fond d'un cratère lunaire",
];

const isBusy = (s, p) =>
  s?.thinking ||
  s?.preview_status === "generating" ||
  p?.status === "generating" ||
  p?.images_status === "generating" ||
  p?.clips_status === "generating" ||
  p?.export_status === "rendering" ||
  (p?.scenes || []).some((sc) => sc.video_status === "generating" || sc.image_status === "generating");

const VOICE_NAMES = {
  sANWqF1bCMzR6eyZbCGw: "Marie", pFZP5JQG7iQjIQuC4Bku: "Lily", Xb7hH8MSUJpSbSDYk0k2: "Alice",
  XrExE9yKIg1WjnnlVkGX: "Matilda", EXAVITQu4vr4xnSDxMaL: "Sarah", JBFqnCBsd6RMkjVDRZzb: "George",
  auq43ws1oslv0tO4BDa7: "Adam", nPczCjzI2devNBz1zQrb: "Brian", onwK4e9ZLuTAKqWW03F9: "Daniel",
  IKne3meq5aSn9XLyUdCD: "Charlie", pqHfZKP75CvOlQylNhV4: "Bill", SAz9YHcvj6GT2YYXdXww: "River",
};
const voiceName = (id) => VOICE_NAMES[id] || null;

export default function Neo() {
  const [session, setSession] = useState(null);
  const [project, setProject] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async (sid, projId) => {
    try {
      const { data: s } = await api.get(`/neo/sessions/${sid}`);
      setSession(s);
      let p = null;
      const pid = s.project_id || projId;
      if (pid) {
        const r = await api.get(`/projects/${pid}`);
        p = r.data;
        setProject(p);
      }
      return { s, p };
    } catch {
      return null;
    }
  }, []);

  const startPolling = useCallback((sid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const res = await refresh(sid);
      if (!res || !isBusy(res.s, res.p)) clearInterval(pollRef.current);
    }, 4000);
  }, [refresh]);

  useEffect(() => {
    const init = async () => {
      const saved = localStorage.getItem("neo_session_id");
      if (saved) {
        const res = await refresh(saved);
        if (res) {
          if (isBusy(res.s, res.p)) startPolling(saved);
          return;
        }
      }
      const { data } = await api.post("/neo/sessions");
      localStorage.setItem("neo_session_id", data.id);
      setSession(data);
    };
    init();
    return () => clearInterval(pollRef.current);
  }, [refresh, startPolling]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages?.length, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending || !session || session.thinking) return;
    setInput("");
    setSending(true);
    setSession((s) => ({ ...s, thinking: true, messages: [...s.messages, { role: "user", content: msg }] }));
    try {
      const { data } = await api.post(`/neo/sessions/${session.id}/message`, { message: msg });
      setSession(data);
      startPolling(session.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neo n'a pas pu répondre, réessayez.");
      setSession((s) => ({ ...s, thinking: false }));
    } finally {
      setSending(false);
    }
  };

  const newFilm = async () => {
    clearInterval(pollRef.current);
    const { data } = await api.post("/neo/sessions");
    localStorage.setItem("neo_session_id", data.id);
    setSession(data);
    setProject(null);
  };

  if (!session)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );

  const brief = session.brief || {};
  const hasBrief = brief.title || brief.logline;

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)] max-w-[1600px] mx-auto w-full">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 space-y-4" data-testid="neo-chat-messages">
          {session.messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-amber-500 text-slate-950 font-medium rounded-br-md"
                    : "bg-[#12151B] border border-white/10 text-slate-200 rounded-bl-md"
                }`}
                data-testid={`neo-message-${m.role}`}
              >
                {m.role === "assistant" && i === 0 && (
                  <span className="flex items-center gap-2 text-amber-400 font-bold text-xs mb-1.5 tracking-widest">
                    <BrainCircuit className="w-4 h-4" /> NEO
                  </span>
                )}
                {m.content}
              </div>
            </div>
          ))}
          {(sending || session.thinking) && (
            <div className="flex justify-start">
              <div className="bg-[#12151B] border border-white/10 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> Neo réfléchit…
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {session.messages.length <= 1 && (
          <div className="px-4 sm:px-8 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid="neo-suggestion-chip"
                onClick={() => send(s)}
                className="text-xs text-slate-400 border border-white/15 hover:border-amber-500/40 hover:text-white rounded-full px-3 py-1.5 transition-colors text-left"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-white/10 p-4 sm:px-8">
          <div className="flex gap-3">
            <input
              data-testid="neo-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={session.thinking}
              placeholder={session.thinking ? "Neo réfléchit…" : "Décrivez votre film, ajustez, puis dites « vas-y »…"}
              className="flex-1 bg-[#12151B] border border-white/10 focus:border-amber-500/50 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors disabled:opacity-60"
            />
            <button
              data-testid="neo-send-button"
              onClick={() => send()}
              disabled={sending || session.thinking || !input.trim()}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold px-5 rounded-xl transition-all active:scale-[0.97] disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <aside className="lg:w-[400px] xl:w-[440px] border-t lg:border-t-0 lg:border-l border-white/10 bg-[#0C0F13] overflow-y-auto max-h-[45vh] lg:max-h-none" data-testid="neo-preview-panel">
        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-mono text-xs tracking-widest text-amber-400 uppercase">Votre film</h2>
            <button data-testid="neo-new-film-button" onClick={newFilm} className="text-xs text-slate-400 hover:text-white underline underline-offset-2">
              Nouveau film
            </button>
          </div>

          <div className="relative aspect-video rounded-xl overflow-hidden bg-black/50 border border-white/10">
            {session.preview_status !== "error" && session.preview_image_url ? (
              <img
                src={mediaUrl(session.preview_image_url)}
                alt="Aperçu du film"
                className="w-full h-full object-cover"
                data-testid="neo-preview-image"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className={`w-full h-full flex flex-col items-center justify-center gap-2 px-4 text-center ${session.preview_status === "error" ? "text-rose-400" : "text-slate-600"}`}>
                {session.preview_status === "error" ? (
                  <>
                    <AlertTriangle className="w-7 h-7" />
                    <span className="text-xs leading-relaxed" data-testid="neo-preview-error">{session.preview_error}</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-8 h-8" />
                    <span className="text-xs">L'aperçu apparaîtra ici</span>
                  </>
                )}
              </div>
            )}
            {session.preview_status === "generating" && (
              <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2">
                <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
                <span className="text-xs text-slate-300">Génération de l'aperçu…</span>
              </div>
            )}
          </div>

          {hasBrief && (
            <div className="bg-[#12151B] border border-white/10 rounded-xl p-4 space-y-2" data-testid="neo-brief-card">
              <h3 className="font-display font-extrabold text-lg text-white leading-tight">{brief.title || "Sans titre"}</h3>
              {brief.logline && <p className="text-xs text-slate-300 italic">{brief.logline}</p>}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {brief.genre && <span className="text-[10px] font-mono tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded px-2 py-0.5">{brief.genre.toUpperCase()}</span>}
                {brief.style && <span className="text-[10px] font-mono tracking-wider bg-rose-500/15 text-rose-400 border border-rose-500/30 rounded px-2 py-0.5">{brief.style.toUpperCase()}</span>}
                {brief.duration_min && <span className="text-[10px] font-mono tracking-wider bg-white/5 text-slate-300 border border-white/15 rounded px-2 py-0.5">{brief.duration_min} MIN</span>}
              </div>
            </div>
          )}

          {project && (
            <div className="bg-[#12151B] border border-white/10 rounded-xl p-4 space-y-3" data-testid="neo-project-progress">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white">Production</h3>
                <Link to={`/film/${project.id}`} data-testid="neo-open-project-link" className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                  Page détaillée <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <ProgressRow label="Scénario" done={project.status === "ready"} active={project.status === "generating"}
                detail={project.status === "ready" ? `${project.scene_count} scènes` : project.status === "error" ? "échec" : "écriture…"} />
              <ProgressRow label="Images" done={project.scene_count > 0 && project.images_done >= project.scene_count}
                active={project.images_status === "generating"}
                detail={`${project.images_done || 0}/${project.scene_count || "?"}`} />
              <ProgressRow label="Clips animés" done={project.scene_count > 0 && (project.clips_done || 0) >= project.scene_count}
                active={project.clips_status === "generating"}
                error={project.clips_status === "done" && (project.clips_failed || 0) > 0}
                detail={project.clips_status === "generating" ? `${project.clips_done || 0}/${project.scene_count || "?"}`
                  : (project.clips_failed || 0) > 0 ? `${project.clips_done || 0}/${project.scene_count} — échec`
                  : `${project.clips_done || 0}/${project.scene_count || "?"}`} />
              {project.clips_status === "done" && (project.clips_failed || 0) > 0 && project.clips_error && (
                <p className="text-[11px] text-rose-400 leading-snug -mt-1" data-testid="neo-clips-error">
                  {project.clips_error}
                </p>
              )}
              <ProgressRow label="Film final" done={project.export_status === "done"}
                active={project.export_status === "rendering"}
                detail={project.export_status === "rendering" ? project.export_progress || "montage…" : project.export_status === "done" ? "prêt" : "—"} />

              {project.status === "ready" && (project.characters || []).length > 0 && (
                <div className="pt-1" data-testid="neo-voice-cast">
                  <p className="text-[10px] font-mono tracking-widest text-slate-500 uppercase mb-1.5">Casting vocal</p>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-slate-400">🎙️ Narrateur</span>
                      <span className="text-amber-400/80 font-mono">Victoria</span>
                    </div>
                    {(project.characters || []).slice(0, 6).map((c) => (
                      <div key={c.name} className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-300 truncate mr-2">{c.name}</span>
                        <span className="text-slate-500 font-mono">{(project.voice_map && voiceName(project.voice_map[c.name?.toUpperCase()])) || "voix IA"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {project.status === "ready" && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <a href={downloadUrl(project.id, "pdf")} data-testid="neo-download-pdf"
                     className="flex items-center gap-1.5 text-xs border border-white/15 hover:border-amber-500/40 text-slate-300 hover:text-white rounded-lg px-2.5 py-1.5 transition-colors">
                    <FileDown className="w-3.5 h-3.5" /> Script PDF
                  </a>
                  <a href={downloadUrl(project.id, "txt")} data-testid="neo-download-txt"
                     className="flex items-center gap-1.5 text-xs border border-white/15 hover:border-amber-500/40 text-slate-300 hover:text-white rounded-lg px-2.5 py-1.5 transition-colors">
                    <FileText className="w-3.5 h-3.5" /> Script TXT
                  </a>
                </div>
              )}

              {project.export_status === "done" && project.export_url && (
                <div className="pt-1 space-y-2">
                  <video controls className="w-full rounded-lg bg-black" src={mediaUrl(project.export_url)} data-testid="neo-export-player" />
                  <a href={mediaUrl(project.export_url)} download data-testid="neo-download-mp4"
                     className="flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 text-xs font-bold rounded-lg px-3 py-2.5 hover:from-amber-400 hover:to-amber-500">
                    <Download className="w-3.5 h-3.5" /> Télécharger le film MP4
                  </a>
                </div>
              )}
            </div>
          )}

          {!hasBrief && !project && (
            <p className="text-xs text-slate-500 leading-relaxed">
              Décrivez votre idée dans la conversation. Neo proposera un titre, un synopsis et un aperçu visuel — puis dites « vas-y » pour lancer la création du film.
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}

const ProgressRow = ({ label, done, active, error, detail }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="flex items-center gap-2 text-slate-300">
      <span className={`w-2 h-2 rounded-full ${error ? "bg-rose-500" : done ? "bg-emerald-400" : active ? "bg-amber-400 animate-pulse" : "bg-slate-600"}`} />
      {label}
    </span>
    <span className={`font-mono ${error ? "text-rose-400" : active ? "text-amber-400" : "text-slate-500"}`}>
      {active && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
      {detail}
    </span>
  </div>
);
